import { useMemo } from 'react';
import { OrderReferralData } from '../store/apis/orders';

export interface ReferralTotals {
  totalAmount: number;
  totalTickets: number;
  totalOrders: number;
}

export interface ReferralPromo {
  milestones: { tickets: number; percent: number }[];
  ticketsSold: number;
  currentReward: number;
  next: { tickets: number; percent: number } | null;
  ticketsToNext: number;
  overallProgress: number;
}

export const useReferralTotals = (
  data: OrderReferralData | null,
  referredOrders: {
    buyer_name: string;
    amount: number;
    created_at: string;
    items_count: number;
  }[],
): ReferralTotals => useMemo(() => {
  const totalAmount = data?.order.owner_referral?.amount_sold || 0;
  const totalTickets = data?.order.owner_referral?.tickets_sold || 0;
  const totalOrders = referredOrders.length;

  return { totalAmount, totalTickets, totalOrders };
}, [data, referredOrders]);

export const useReferralPromo = (totalTickets: number): ReferralPromo => useMemo(() => {
  const milestones = [
    { tickets: 2, percent: 20 },
    { tickets: 5, percent: 50 },
    { tickets: 7, percent: 70 },
    { tickets: 10, percent: 100 },
  ];

  const ticketsSold = totalTickets || 0;
  const achieved = milestones.filter((m) => ticketsSold >= m.tickets);
  const currentReward = achieved.length > 0 ? achieved[achieved.length - 1].percent : 0;
  const next = milestones.find((m) => ticketsSold < m.tickets) || null;
  const ticketsToNext = next ? Math.max(0, next.tickets - ticketsSold) : 0;
  const overallProgress = Math.min(100, Math.max(0, (ticketsSold / 10) * 100));

  return {
    milestones,
    ticketsSold,
    currentReward,
    next,
    ticketsToNext,
    overallProgress,
  };
}, [totalTickets]);

// Keep the original functions for cases where useMemo is not needed
export const calculateReferralTotals = (
  data: OrderReferralData | null,
  referredOrders: {
    buyer_name: string;
    amount: number;
    created_at: string;
    items_count: number;
  }[],
): ReferralTotals => {
  const totalAmount = data?.order.owner_referral?.amount_sold || 0;
  const totalTickets = data?.order.owner_referral?.tickets_sold || 0;
  const totalOrders = referredOrders.length;

  return { totalAmount, totalTickets, totalOrders };
};

export const calculateReferralPromo = (totalTickets: number): ReferralPromo => {
  const milestones = [
    { tickets: 2, percent: 20 },
    { tickets: 5, percent: 50 },
    { tickets: 7, percent: 70 },
    { tickets: 10, percent: 100 },
  ];

  const ticketsSold = totalTickets || 0;
  const achieved = milestones.filter((m) => ticketsSold >= m.tickets);
  const currentReward = achieved.length > 0 ? achieved[achieved.length - 1].percent : 0;
  const next = milestones.find((m) => ticketsSold < m.tickets) || null;
  const ticketsToNext = next ? Math.max(0, next.tickets - ticketsSold) : 0;
  const overallProgress = Math.min(100, Math.max(0, (ticketsSold / 10) * 100));

  return {
    milestones,
    ticketsSold,
    currentReward,
    next,
    ticketsToNext,
    overallProgress,
  };
};
